import {
  adminWallTimeToUtc,
  getAdminFiguresZonedParts,
} from "./adminFiguresTime.tsx";

/** New Performance clock from this calendar day onward (America/Los_Angeles). */
export const PERFORMANCE_PACIFIC_TZ = "America/Los_Angeles";

/** Historical Performance days stay on the previous Eastern clock. */
export const PERFORMANCE_LEGACY_TZ = "America/New_York";

/** Historical Eastern weeks kept the old Monday 4:00 AM roll. */
const PERFORMANCE_LEGACY_WEEK_START_HOUR = 4;

/**
 * Pacific Performance days roll at 3:00 AM through Oct 6 2026.
 * From Oct 7 they roll at midnight. Oct 6 before 3:00 AM stays on Oct 5.
 */
export const PERFORMANCE_PACIFIC_DAY_START_HOUR = 3;
const PERFORMANCE_MIDNIGHT_AFTER = { year: 2026, month: 10, day: 6 } as const;

/**
 * First Performance day that uses Pacific time.
 * Days before this stay America/New_York, matching the previous figures clock.
 */
export const PERFORMANCE_PACIFIC_SINCE = { year: 2026, month: 9, day: 6 } as const;

export const PERFORMANCE_PACIFIC_CUTOFF_KEY = `${PERFORMANCE_PACIFIC_SINCE.year}-${String(PERFORMANCE_PACIFIC_SINCE.month).padStart(2, "0")}-${String(PERFORMANCE_PACIFIC_SINCE.day).padStart(2, "0")}`;

const pacificResetHour = (year: number, month: number, day: number): number => {
  const since = PERFORMANCE_MIDNIGHT_AFTER;
  const usesMidnight =
    year > since.year ||
    (year === since.year && month > since.month) ||
    (year === since.year && month === since.month && day > since.day);
  return usesMidnight ? 0 : PERFORMANCE_PACIFIC_DAY_START_HOUR;
};

const dayStartHour = (timeZone: string, year = 1, month = 1, day = 1): number =>
  timeZone === PERFORMANCE_PACIFIC_TZ ? pacificResetHour(year, month, day) : 0;

const weekStartHour = (timeZone: string, year = 1, month = 1, day = 1): number =>
  timeZone === PERFORMANCE_PACIFIC_TZ ?
    pacificResetHour(year, month, day) :
    PERFORMANCE_LEGACY_WEEK_START_HOUR;

const wallDayStart = (year: number, month: number, day: number, timeZone: string): Date =>
  adminWallTimeToUtc(year, month, day, dayStartHour(timeZone, year, month, day), 0, 0, 0, timeZone);

const WEEKDAY_TO_MONDAY_INDEX: Record<string, number> = {
  Mon: 0,
  Tue: 1,
  Wed: 2,
  Thu: 3,
  Fri: 4,
  Sat: 5,
  Sun: 6,
};

export const getPerformancePacificCutoff = (): Date =>
  adminWallTimeToUtc(
    PERFORMANCE_PACIFIC_SINCE.year,
    PERFORMANCE_PACIFIC_SINCE.month,
    PERFORMANCE_PACIFIC_SINCE.day,
    PERFORMANCE_PACIFIC_DAY_START_HOUR,
    0,
    0,
    0,
    PERFORMANCE_PACIFIC_TZ,
  );

export const getPerformanceTimeZone = (date: Date): string =>
  date.getTime() >= getPerformancePacificCutoff().getTime()
    ? PERFORMANCE_PACIFIC_TZ
    : PERFORMANCE_LEGACY_TZ;

export const getPerformanceDayStart = (date: Date, timeZone: string): Date => {
  const parts = getAdminFiguresZonedParts(date, timeZone);
  const hour = dayStartHour(timeZone, parts.year, parts.month, parts.day);
  if (hour > 0 && parts.hour < hour) {
    const mid = adminWallTimeToUtc(parts.year, parts.month, parts.day, 12, 0, 0, 0, timeZone);
    mid.setTime(mid.getTime() - 24 * 60 * 60 * 1000);
    const prev = getAdminFiguresZonedParts(mid, timeZone);
    return wallDayStart(prev.year, prev.month, prev.day, timeZone);
  }
  return wallDayStart(parts.year, parts.month, parts.day, timeZone);
};

export const addPerformanceCalendarDays = (date: Date, days: number, timeZone: string): Date => {
  const start = getPerformanceDayStart(date, timeZone);
  const { year, month, day } = getAdminFiguresZonedParts(start, timeZone);
  const mid = adminWallTimeToUtc(year, month, day, 12, 0, 0, 0, timeZone);
  mid.setTime(mid.getTime() + days * 24 * 60 * 60 * 1000);
  return getPerformanceDayStart(mid, timeZone);
};

export const getPerformanceDayEnd = (date: Date, timeZone: string): Date => {
  const nextStart = addPerformanceCalendarDays(getPerformanceDayStart(date, timeZone), 1, timeZone);
  return new Date(nextStart.getTime() - 1);
};

export const getPerformanceDateKey = (date: Date, timeZone: string): string => {
  const start = getPerformanceDayStart(date, timeZone);
  const { year, month, day } = getAdminFiguresZonedParts(start, timeZone);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const getPerformanceMonthStart = (date: Date, monthsAgo: number, timeZone: string): Date => {
  const parts = getAdminFiguresZonedParts(getPerformanceDayStart(date, timeZone), timeZone);
  const totalMonths = parts.year * 12 + (parts.month - 1) - monthsAgo;
  const year = Math.floor(totalMonths / 12);
  const month = (totalMonths % 12) + 1;
  return adminWallTimeToUtc(year, month, 1, dayStartHour(timeZone, year, month, 1), 0, 0, 0, timeZone);
};

export const getPerformanceMonthKey = (date: Date, timeZone: string): string => {
  const { year, month } = getAdminFiguresZonedParts(getPerformanceDayStart(date, timeZone), timeZone);
  return `${year}-${String(month).padStart(2, "0")}`;
};

export const getPerformanceYearKey = (date: Date, timeZone: string): string =>
  String(getAdminFiguresZonedParts(getPerformanceDayStart(date, timeZone), timeZone).year);

const businessDayStart = (date: Date, timeZone: string): Date => {
  const parts = getAdminFiguresZonedParts(date, timeZone);
  const hour = weekStartHour(timeZone, parts.year, parts.month, parts.day);
  const dayStart = adminWallTimeToUtc(parts.year, parts.month, parts.day, hour, 0, 0, 0, timeZone);
  if (parts.hour < hour) {
    return addPerformanceCalendarDays(dayStart, -1, timeZone);
  }
  return dayStart;
};

const weekdayIndex = (date: Date, timeZone: string): number => {
  const { weekday } = getAdminFiguresZonedParts(businessDayStart(date, timeZone), timeZone);
  return WEEKDAY_TO_MONDAY_INDEX[weekday] ?? 0;
};

export const getPerformanceWeekStart = (date: Date, timeZone: string): Date => {
  const businessDay = businessDayStart(date, timeZone);
  const monday = addPerformanceCalendarDays(businessDay, -weekdayIndex(date, timeZone), timeZone);
  const { year, month, day } = getAdminFiguresZonedParts(monday, timeZone);
  return adminWallTimeToUtc(year, month, day, weekStartHour(timeZone, year, month, day), 0, 0, 0, timeZone);
};

export const getPerformanceWeekEnd = (weekStart: Date, timeZone: string): Date => {
  const next = addPerformanceCalendarDays(getPerformanceDayStart(weekStart, timeZone), 7, timeZone);
  const { year, month, day } = getAdminFiguresZonedParts(next, timeZone);
  return new Date(
    adminWallTimeToUtc(year, month, day, weekStartHour(timeZone, year, month, day), 0, 0, 0, timeZone).getTime() - 1,
  );
};

/** Move an already-aligned Monday week start by whole weeks. Do not re-run getPerformanceWeekStart: Monday before the week-start hour would roll back to the previous week and skip every other week. */
export const shiftPerformanceWeekStart = (weekStart: Date, weeks: number, timeZone: string): Date => {
  const { year, month, day } = getAdminFiguresZonedParts(weekStart, timeZone);
  const mid = adminWallTimeToUtc(year, month, day, 12, 0, 0, 0, timeZone);
  mid.setTime(mid.getTime() + weeks * 7 * 24 * 60 * 60 * 1000);
  const shifted = getAdminFiguresZonedParts(mid, timeZone);
  return adminWallTimeToUtc(
    shifted.year,
    shifted.month,
    shifted.day,
    weekStartHour(timeZone, shifted.year, shifted.month, shifted.day),
    0,
    0,
    0,
    timeZone,
  );
};

export const getPerformanceWeekKey = (date: Date, timeZone: string): string => {
  const weekStart = getPerformanceWeekStart(date, timeZone);
  return getPerformanceDateKey(weekStart, timeZone);
};

export const formatPerformanceDateLabel = (
  date: Date,
  timeZone: string,
  options: Intl.DateTimeFormatOptions,
): string => date.toLocaleDateString("en-US", { ...options, timeZone });

export const formatPerformanceDateTime = (date: Date, timeZone = getPerformanceTimeZone(date)): string =>
  date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZone,
  });

export const getPerformanceEventDateKey = (date: Date): string =>
  getPerformanceDateKey(date, getPerformanceTimeZone(date));

export const getPerformanceEventWeekKey = (date: Date): string =>
  getPerformanceWeekKey(date, getPerformanceTimeZone(date));

export const getPerformanceEventMonthKey = (date: Date): string =>
  getPerformanceMonthKey(date, getPerformanceTimeZone(date));

export const getPerformanceEventYearKey = (date: Date): string =>
  getPerformanceYearKey(date, getPerformanceTimeZone(date));

export type PerformancePeriod = {
  key: string;
  start: Date;
  end: Date;
  timeZone: string;
};

const parseDateKey = (key: string): { year: number; month: number; day: number } => {
  const [year, month, day] = key.split("-").map(Number);
  return { year, month, day };
};

/** Inclusive range for a daily Performance row (Pacific from the cutoff day onward). */
export const getPerformanceDailyRangeFromKey = (key: string): { start: Date; end: Date; timeZone: string } => {
  const { year, month, day } = parseDateKey(key);

  if (key > PERFORMANCE_PACIFIC_CUTOFF_KEY) {
    const start = wallDayStart(year, month, day, PERFORMANCE_PACIFIC_TZ);
    return { start, end: getPerformanceDayEnd(start, PERFORMANCE_PACIFIC_TZ), timeZone: PERFORMANCE_PACIFIC_TZ };
  }

  if (key === PERFORMANCE_PACIFIC_CUTOFF_KEY) {
    const start = adminWallTimeToUtc(year, month, day, 0, 0, 0, 0, PERFORMANCE_LEGACY_TZ);
    const pacificStart = wallDayStart(year, month, day, PERFORMANCE_PACIFIC_TZ);
    return {
      start,
      end: getPerformanceDayEnd(pacificStart, PERFORMANCE_PACIFIC_TZ),
      timeZone: PERFORMANCE_PACIFIC_TZ,
    };
  }

  const start = adminWallTimeToUtc(year, month, day, 0, 0, 0, 0, PERFORMANCE_LEGACY_TZ);
  return { start, end: getPerformanceDayEnd(start, PERFORMANCE_LEGACY_TZ), timeZone: PERFORMANCE_LEGACY_TZ };
};

export const getPerformanceDailyPeriods = (now = new Date(), count = 28): PerformancePeriod[] => {
  const cutoff = getPerformancePacificCutoff();
  const periods: PerformancePeriod[] = [];
  const used = new Set<string>();

  const push = (period: PerformancePeriod) => {
    if (used.has(period.key) || periods.length >= count) return;
    used.add(period.key);
    periods.push(period);
  };

  if (now.getTime() < cutoff.getTime()) {
    let day = getPerformanceDayStart(now, PERFORMANCE_LEGACY_TZ);
    while (periods.length < count) {
      const key = getPerformanceDateKey(day, PERFORMANCE_LEGACY_TZ);
      const range = getPerformanceDailyRangeFromKey(key);
      push({ key, ...range });
      day = addPerformanceCalendarDays(day, -1, PERFORMANCE_LEGACY_TZ);
    }
    return periods;
  }

  let day = getPerformanceDayStart(now, PERFORMANCE_PACIFIC_TZ);
  while (periods.length < count && day.getTime() >= cutoff.getTime()) {
    const key = getPerformanceDateKey(day, PERFORMANCE_PACIFIC_TZ);
    const range = getPerformanceDailyRangeFromKey(key);
    push({ key, ...range });
    day = addPerformanceCalendarDays(day, -1, PERFORMANCE_PACIFIC_TZ);
  }

  let easternDay = getPerformanceDayStart(new Date(cutoff.getTime() - 1), PERFORMANCE_LEGACY_TZ);
  if (getPerformanceDateKey(easternDay, PERFORMANCE_LEGACY_TZ) === PERFORMANCE_PACIFIC_CUTOFF_KEY) {
    easternDay = addPerformanceCalendarDays(easternDay, -1, PERFORMANCE_LEGACY_TZ);
  }

  while (periods.length < count) {
    const key = getPerformanceDateKey(easternDay, PERFORMANCE_LEGACY_TZ);
    const range = getPerformanceDailyRangeFromKey(key);
    push({ key, ...range });
    easternDay = addPerformanceCalendarDays(easternDay, -1, PERFORMANCE_LEGACY_TZ);
  }

  return periods;
};

export const getPerformanceWeeklyPeriods = (now = new Date(), count = 12): PerformancePeriod[] => {
  const cutoff = getPerformancePacificCutoff();
  const periods: PerformancePeriod[] = [];
  const used = new Set<string>();

  const push = (period: PerformancePeriod) => {
    if (used.has(period.key) || periods.length >= count) return;
    used.add(period.key);
    periods.push(period);
  };

  if (now.getTime() >= cutoff.getTime()) {
    let start = getPerformanceWeekStart(now, PERFORMANCE_PACIFIC_TZ);
    push({
      key: getPerformanceDateKey(start, PERFORMANCE_PACIFIC_TZ),
      start,
      end: getPerformanceWeekEnd(start, PERFORMANCE_PACIFIC_TZ),
      timeZone: PERFORMANCE_PACIFIC_TZ,
    });
    start = shiftPerformanceWeekStart(start, -1, PERFORMANCE_PACIFIC_TZ);
    while (periods.length < count && start.getTime() >= cutoff.getTime()) {
      push({
        key: getPerformanceDateKey(start, PERFORMANCE_PACIFIC_TZ),
        start,
        end: getPerformanceWeekEnd(start, PERFORMANCE_PACIFIC_TZ),
        timeZone: PERFORMANCE_PACIFIC_TZ,
      });
      start = shiftPerformanceWeekStart(start, -1, PERFORMANCE_PACIFIC_TZ);
    }
  }

  let easternStart = getPerformanceWeekStart(new Date(cutoff.getTime() - 1), PERFORMANCE_LEGACY_TZ);
  while (periods.length < count) {
    push({
      key: getPerformanceDateKey(easternStart, PERFORMANCE_LEGACY_TZ),
      start: easternStart,
      end: getPerformanceWeekEnd(easternStart, PERFORMANCE_LEGACY_TZ),
      timeZone: PERFORMANCE_LEGACY_TZ,
    });
    easternStart = shiftPerformanceWeekStart(easternStart, -1, PERFORMANCE_LEGACY_TZ);
  }

  return periods;
};

export const getPerformanceMonthlyPeriods = (now = new Date(), count = 12): PerformancePeriod[] => {
  const cutoff = getPerformancePacificCutoff();
  const periods: PerformancePeriod[] = [];
  const used = new Set<string>();

  const push = (period: PerformancePeriod) => {
    if (used.has(period.key) || periods.length >= count) return;
    used.add(period.key);
    periods.push(period);
  };

  if (now.getTime() >= cutoff.getTime()) {
    let monthsAgo = 0;
    let start = getPerformanceMonthStart(now, monthsAgo, PERFORMANCE_PACIFIC_TZ);
    while (periods.length < count) {
      const key = getPerformanceMonthKey(start, PERFORMANCE_PACIFIC_TZ);
      const next = getPerformanceMonthStart(now, monthsAgo + 1, PERFORMANCE_PACIFIC_TZ);
      push({
        key,
        start,
        end: new Date(next.getTime() - 1),
        timeZone: PERFORMANCE_PACIFIC_TZ,
      });
      if (start.getTime() < cutoff.getTime()) break;
      monthsAgo += 1;
      start = next;
    }
  }

  let easternMonthsAgo = 0;
  const easternAnchor = new Date(Math.min(now.getTime(), cutoff.getTime() - 1));
  while (periods.length < count) {
    const start = getPerformanceMonthStart(easternAnchor, easternMonthsAgo, PERFORMANCE_LEGACY_TZ);
    const next = getPerformanceMonthStart(easternAnchor, easternMonthsAgo + 1, PERFORMANCE_LEGACY_TZ);
    push({
      key: getPerformanceMonthKey(start, PERFORMANCE_LEGACY_TZ),
      start,
      end: new Date(next.getTime() - 1),
      timeZone: PERFORMANCE_LEGACY_TZ,
    });
    easternMonthsAgo += 1;
  }

  return periods;
};

export const getPerformanceYearlyPeriods = (now = new Date(), count = 3): PerformancePeriod[] => {
  const cutoff = getPerformancePacificCutoff();
  const periods: PerformancePeriod[] = [];
  const used = new Set<string>();

  const push = (period: PerformancePeriod) => {
    if (used.has(period.key) || periods.length >= count) return;
    used.add(period.key);
    periods.push(period);
  };

  const currentTz = now.getTime() >= cutoff.getTime() ? PERFORMANCE_PACIFIC_TZ : PERFORMANCE_LEGACY_TZ;
  const currentYear = Number(getPerformanceYearKey(now, currentTz));

  for (let i = 0; i < count; i++) {
    const year = currentYear - i;
    const tz = year >= PERFORMANCE_PACIFIC_SINCE.year ? currentTz : PERFORMANCE_LEGACY_TZ;
    const start = adminWallTimeToUtc(year, 1, 1, dayStartHour(tz, year, 1, 1), 0, 0, 0, tz);
    const end = new Date(adminWallTimeToUtc(year + 1, 1, 1, dayStartHour(tz, year + 1, 1, 1), 0, 0, 0, tz).getTime() - 1);
    push({ key: String(year), start, end, timeZone: tz });
  }

  return periods;
};
